/**
 * AstraeaMesh Service
 * 
 * Provides API client for mesh network management from Virgo dashboard.
 */

import api from './api';

export const meshService = {
  // ==================== Nodes ====================
  
  /**
   * Get list of all mesh nodes
   */
  getNodes: async () => {
    const response = await api.get('/v1/mesh/nodes');
    return response.data;
  },
  
  /**
   * Get details for a specific node
   */
  getNode: async (fingerprint) => {
    const response = await api.get(`/v1/mesh/nodes/${fingerprint}`);
    return response.data;
  },
  
  /**
   * Revoke a mesh node
   */
  revokeNode: async (fingerprint) => {
    const response = await api.delete(`/v1/mesh/nodes/${fingerprint}`);
    return response.data;
  },
  
  // ==================== Network ====================
  
  /**
   * Get network overview statistics
   */
  getNetworkOverview: async () => {
    const response = await api.get('/v1/mesh/network/overview');
    return response.data;
  },
  
  // ==================== Auth Keys ====================
  
  /**
   * Get list of all auth keys
   */
  getAuthKeys: async () => {
    const response = await api.get('/v1/mesh/auth-keys');
    return response.data;
  },
  
  /**
   * Create a new auth key
   * @param {Object} keyData - Key configuration
   * @param {string} keyData.organization_id - Organization ID
   * @param {string} keyData.description - Key description
   * @param {string} keyData.key_type - 'reusable', 'single-use', or 'preauthorized'
   * @param {string[]} keyData.tags - Tags to assign to enrolled nodes
   * @param {number} keyData.expiry_days - Expiration in days
   * @param {number} keyData.max_uses - Maximum uses (0 = unlimited)
   */
  createAuthKey: async (keyData) => {
    const response = await api.post('/v1/mesh/auth-keys', keyData);
    return response.data;
  },
  
  /**
   * Get details for a specific auth key
   */
  getAuthKey: async (keyId) => {
    const response = await api.get(`/v1/mesh/auth-keys/${keyId}`);
    return response.data;
  },
  
  /**
   * Revoke an auth key
   */
  revokeAuthKey: async (keyId) => {
    const response = await api.delete(`/v1/mesh/auth-keys/${keyId}`);
    return response.data;
  },
  
  // ==================== Sites ====================
  
  /**
   * Get list of all sites/offices
   */
  getSites: async () => {
    const response = await api.get('/v1/mesh/sites');
    return response.data;
  },
  
  /**
   * Create a new site
   * @param {Object} siteData - Site configuration
   * @param {string} siteData.name - Site name
   * @param {string} siteData.organization_id - Organization ID
   * @param {string} siteData.mesh_subnet - Mesh subnet (e.g., '10.100.1.0/24')
   * @param {string[]} siteData.advertised_routes - Routes to advertise
   */
  createSite: async (siteData) => {
    const response = await api.post('/v1/mesh/sites', siteData);
    return response.data;
  },
  
  // ==================== WebSocket ====================
  
  /**
   * Connect to mesh events WebSocket
   * @param {Function} onMessage - Callback for received messages
   * @returns {WebSocket} WebSocket connection
   */
  connectWebSocket: (onMessage) => {
    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}${window.location.host}/api/v1/mesh/ws`;
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (e) {
        console.error('Failed to parse mesh event:', e);
      }
    };
    
    ws.onerror = (error) => {
      console.error('Mesh WebSocket error:', error);
    };
    
    return ws;
  }
};

export default meshService;
